import { HumanCallHttp } from './direct-call-frames';

/**
 * US 37334 (Feature 37328 S6): humans in a Roost call connect peer to peer over WebRTC. The bridge
 * (US 37333) only relays signals between two humans of the same call: a client POSTs to
 * `rtc/signal`, and the target hears SignalR `rtcSignal`. Media never touches the bridge.
 */
export type RtcSignalType =
  'offer' | 'answer' | 'ice' | 'hangup' | 'media-state';

export interface RtcSignal {
  conversationId: string;
  /** The sender's subject, `entra:{oid}`, as the bridge stamped it. */
  from: string;
  type: RtcSignalType;
  payload: Record<string, unknown> | null;
}

export interface RtcIceServer {
  urls: string[];
}

/** `GET rtc/config`: who this human is, every other human in the call, STUN, and the mesh caps. */
export interface RtcConfig {
  self: string;
  peers: string[];
  iceServers: RtcIceServer[];
  maxVideoHumans: number;
  maxAudioHumans: number;
}

/** The bridge's ice-outcome vocabulary (RtcSignalPolicy); anything else is refused there. */
export type RtcIceOutcome = 'host' | 'srflx' | 'prflx' | 'relay' | 'failed';
export type RtcNetwork = 'wifi' | 'cellular' | 'ethernet' | 'unknown';
export type RtcPlatform = 'web' | 'ios' | 'android';

/** What became of a posted signal: forwarded, the call is off or closed to us, or throttled. */
export type RtcPostOutcome = 'sent' | 'disabled' | 'refused' | 'throttled';

const SIGNAL_TYPES: ReadonlySet<string> = new Set([
  'offer',
  'answer',
  'ice',
  'hangup',
  'media-state',
]);
const NEEDS_PAYLOAD: ReadonlySet<string> = new Set(['offer', 'answer', 'ice']);
const CONVERSATION_ID = /^[a-zA-Z0-9_-]+$/;

/** The same subject comparison the bridge makes: ordinal, case-insensitive. */
export function sameSubject(a: string, b: string): boolean {
  return a.toLowerCase() === b.toLowerCase();
}

function isObject(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

/** A well-formed relayed signal, or null. The bridge is the authority; this drops junk early. */
export function parseRtcSignal(value: unknown): RtcSignal | null {
  if (!isObject(value)) return null;
  const { conversationId, from, type, payload } = value;
  if (
    typeof conversationId !== 'string' ||
    !CONVERSATION_ID.test(conversationId)
  )
    return null;
  if (typeof from !== 'string' || !from.trim()) return null;
  if (typeof type !== 'string' || !SIGNAL_TYPES.has(type)) return null;
  if (NEEDS_PAYLOAD.has(type) && !isObject(payload)) return null;
  return {
    conversationId,
    from: from.trim(),
    type: type as RtcSignalType,
    payload: isObject(payload) ? payload : null,
  };
}

function positiveInt(value: unknown): number | null {
  return typeof value === 'number' && Number.isInteger(value) && value > 0
    ? value
    : null;
}

/** The config the bridge served, or null when it is not one (an older bridge has no self/peers). */
export function parseRtcConfig(value: unknown): RtcConfig | null {
  if (!isObject(value)) return null;
  const { self, peers, iceServers } = value;
  const maxVideoHumans = positiveInt(value['maxVideoHumans']);
  const maxAudioHumans = positiveInt(value['maxAudioHumans']);
  if (typeof self !== 'string' || !self.trim()) return null;
  if (!Array.isArray(peers) || !Array.isArray(iceServers)) return null;
  if (maxVideoHumans === null || maxAudioHumans === null) return null;
  const others: string[] = [];
  for (const p of peers)
    if (
      typeof p === 'string' &&
      p.trim() &&
      !sameSubject(p, self) &&
      !others.some((o) => sameSubject(o, p))
    )
      others.push(p.trim());
  const servers: RtcIceServer[] = [];
  for (const s of iceServers) {
    if (!isObject(s)) continue;
    const urls = (Array.isArray(s['urls']) ? s['urls'] : [s['urls']]).filter(
      (u): u is string => typeof u === 'string' && /^stuns?:/i.test(u)
    );
    if (urls.length) servers.push({ urls });
  }
  return {
    self: self.trim(),
    peers: others,
    iceServers: servers,
    maxVideoHumans,
    maxAudioHumans,
  };
}

function postOutcome(status: number, ok: boolean): RtcPostOutcome {
  if (ok) return 'sent';
  if (status === 404) return 'disabled';
  if (status === 429) return 'throttled';
  return 'refused';
}

/** The human call HTTP client plus the S5 WebRTC routes (kept apart: the base file is at its size cap). */
export class HumanRtcHttp extends HumanCallHttp {
  async getRtcConfig(
    id: string,
    signal: AbortSignal
  ): Promise<RtcConfig | null> {
    const response = await this.send(
      `/conversations/${this.conversationId(id)}/rtc/config`,
      'GET',
      signal
    );
    return response.ok ? parseRtcConfig(await response.json()) : null;
  }

  async postSignal(
    id: string,
    to: string,
    type: RtcSignalType,
    payload: Record<string, unknown> | null,
    signal: AbortSignal
  ): Promise<RtcPostOutcome> {
    const response = await this.send(
      `/conversations/${this.conversationId(id)}/rtc/signal`,
      'POST',
      signal,
      payload === null ? { to, type } : { to, type, payload }
    );
    return postOutcome(response.status, response.ok);
  }

  /** One line per peer pair for the TURN decision (brief 37328 §3); the offering side reports. */
  async reportIceOutcome(
    id: string,
    to: string,
    report: { outcome: RtcIceOutcome; net: RtcNetwork; platform: RtcPlatform },
    signal: AbortSignal
  ): Promise<RtcPostOutcome> {
    const response = await this.send(
      `/conversations/${this.conversationId(id)}/rtc/signal`,
      'POST',
      signal,
      { to, type: 'ice-outcome', ...report }
    );
    return postOutcome(response.status, response.ok);
  }
}

/**
 * Holds the app's rtcSignal handlers across call connections, like FrameRequestFeed: each new call
 * hub is attached once, and a handler registered once hears signals on every connection.
 */
export class RtcSignalFeed {
  private readonly handlers = new Set<(signal: RtcSignal) => void>();

  on(handler: (signal: RtcSignal) => void): () => void {
    this.handlers.add(handler);
    return () => this.handlers.delete(handler);
  }

  attach(hub: {
    on(type: string, handler: (value: unknown) => void): () => void;
  }): () => void {
    return hub.on('rtcSignal', (value) => {
      const signal = parseRtcSignal(value);
      if (signal) for (const handler of [...this.handlers]) handler(signal);
    });
  }
}
